import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import pinPrompt from "../utils/pinPrompt";
import openLinkedPdf from "../utils/openLinkedPdf";
import { podSendNote } from "../utils/emailOutcome";
import { emailSenderHeaders } from "../utils/emailSender";
import {
  POD_KIND_LABEL as KIND_LABEL, defaultKindFor, isManifestDoc, apiJson, saveDeliveryRecord,
} from "../features/salesOrders/pod";
import { podEligiblePallets } from "../features/salesOrders/palletRules";

// The requests live in features/salesOrders/pod.js (shared with the phone's camera photos).
export { defaultKindFor, isManifestDoc, localInputToIso } from "../features/salesOrders/pod";

/**
 * Proof of delivery / proof of pickup photos for a packing plan.
 *
 * Photos are taken on our iPhones at the handoff and uploaded LATER, here, on
 * the sales order page (no phone workflow — Mike, 2026-09-10). The API reads
 * each photo's EXIF (taken-at, GPS, camera) at confirm time, so the record
 * proves when and where without anyone typing at the dock.
 *
 * Upload path: one presign call → PUT each file straight to the private
 * bucket (XHR for progress) → ONE confirm call per photo. Nothing goes through
 * the Lambda, so phone-sized files never hit the 4.6 MB gateway ceiling.
 *
 * Records are never locked or deleted: hide with a reason.
 */

const SHOP_TZ = "America/Chicago";

export function fmtShop(iso, opts = {}) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: SHOP_TZ, month: "short", day: "numeric", year: opts.year ? "numeric" : undefined,
      hour: "numeric", minute: "2-digit",
    });
  } catch (e) {
    return String(iso);
  }
}

function fmtBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------

export default function PodPanel({ apiBase, planId, salesOrderId, orderNumber, pallets = [], fulfillmentType, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);        // {mode:'new'} | {mode:'add', event} | {mode:'edit', event}
  const [lightbox, setLightbox] = useState(null);  // {event, index}
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    if (!planId) { setData(null); return; }
    setLoading(true); setError("");
    try {
      setData(await apiJson(`${apiBase}/packaging/plans/${planId}/pod`));
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [apiBase, planId]);

  useEffect(() => { load(); }, [load]);

  const eligiblePallets = useMemo(() => podEligiblePallets(pallets), [pallets]); // palletRules (shared)
  const events = data?.events || [];

  const refresh = useCallback(async () => {
    await load();
    if (onChanged) onChanged();
  }, [load, onChanged]);

  const hideEvent = async (ev) => {
    const reason = window.prompt("Hide this delivery record? Nothing is deleted. Reason (required):");
    if (!reason || !reason.trim()) return;
    const pin = await pinPrompt("Enter your PIN to hide this record:");
    if (!pin) return;
    setBusy(`hide_${ev.event_id}`); setError("");
    try {
      await apiJson(`${apiBase}/packaging/plans/${planId}/pod/events/${ev.event_id}/hide`, {
        method: "POST", body: JSON.stringify({ pin, reason: reason.trim() }),
      });
      await refresh();
    } catch (e) { setError(e.message || String(e)); } finally { setBusy(""); }
  };

  const sendPacket = async (ev) => {
    const to = window.prompt("Email the proof-of-delivery packet to:", ev.suggested_email || "");
    if (!to || !to.trim()) return;
    const pin = await pinPrompt("Enter your PIN to send the packet:");
    if (!pin) return;
    setBusy(`send_${ev.event_id}`); setError("");
    try {
      const r = await apiJson(`${apiBase}/packaging/plans/${planId}/pod/events/${ev.event_id}/send`, {
        method: "POST", body: JSON.stringify({ pin, to_email: to.trim() }),
        headers: { "Content-Type": "application/json", ...emailSenderHeaders() },
      });
      // The attempt is always logged to the SO's Email History; email_error means it didn't go out.
      const note = podSendNote(r);
      if (note.ok) window.alert(note.message); else setError(note.message);
    } catch (e) { setError(e.message || String(e)); } finally { setBusy(""); }
  };

  // The packet comes back as a 15-minute storage link, not the file: a packet
  // with four or more photos is over Lambda's 6 MB response limit.
  const openPacket = async (ev) => {
    setBusy(`packet_${ev.event_id}`); setError("");
    try {
      const r = await openLinkedPdf(
        () => apiJson(`${apiBase}/packaging/plans/${planId}/pod/events/${ev.event_id}/packet-link`),
        window, "Building the proof-of-delivery packet…");
      if (r.blocked) setError("Your browser blocked the new tab. Allow pop-ups for this site, then click Packet again.");
    } catch (e) {
      setError(`Couldn't open the packet: ${e.message || String(e)}`);
    } finally { setBusy(""); }
  };

  if (!planId) return null;

  return (
    <div className="card shadow-sm border-top-0 border-end-0 border-bottom-0 border-4 border-primary mt-3">
      <div className="card-header bg-white fw-bold d-flex justify-content-between align-items-center">
        <span>
          <i className="bi bi-camera me-2 text-primary"></i>Proof of delivery
          {data && (
            <span className="badge bg-light text-dark border ms-2 fw-normal">
              {events.length} record{events.length === 1 ? "" : "s"} · {events.reduce((n, e) => n + (e.photo_count || 0), 0)} photos
            </span>
          )}
        </span>
        <button
          className="btn btn-sm btn-primary"
          disabled={!eligiblePallets.length || !!busy}
          title={eligiblePallets.length ? "Upload photos from the handoff and record who received it" : "Photos attach to pallets that have shipped or are staged for pickup"}
          onClick={() => setModal({ mode: "new" })}
        >
          <i className="bi bi-cloud-arrow-up me-1"></i>Add delivery photos
        </button>
      </div>
      <div className="card-body py-2">
        {error && <div className="alert alert-danger py-2 small mb-2">{error}</div>}
        {loading && !data && <div className="text-muted small">Loading…</div>}
        {data && events.length === 0 && (
          <div className="text-muted fst-italic small">No delivery photos yet. Shoot the pallets and the signed manifest on your phone at the handoff, then add them here.</div>
        )}
        {events.map(ev => (
          <PodEventRow
            key={ev.event_id}
            ev={ev}
            busy={busy}
            onOpen={(index) => setLightbox({ event: ev, index })}
            onAddPhotos={() => setModal({ mode: "add", event: ev })}
            onEdit={() => setModal({ mode: "edit", event: ev })}
            onPacket={() => openPacket(ev)}
            onSend={() => sendPacket(ev)}
            onHide={() => hideEvent(ev)}
          />
        ))}
      </div>
      {modal && (
        <PodUploadModal
          apiBase={apiBase} planId={planId} salesOrderId={salesOrderId} orderNumber={orderNumber}
          pallets={eligiblePallets} fulfillmentType={fulfillmentType}
          mode={modal.mode} event={modal.event} limits={data?.limits}
          onClose={() => setModal(null)}
          onDone={async () => { setModal(null); await refresh(); }}
        />
      )}
      {lightbox && (
        <PodLightbox
          apiBase={apiBase} planId={planId} event={lightbox.event} index={lightbox.index}
          onClose={() => setLightbox(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function PodEventRow({ ev, busy, onOpen, onAddPhotos, onEdit, onPacket, onSend, onHide }) {
  const ready = ev.photos.filter(p => p.status === "ready");
  const pending = ev.photos.filter(p => p.status !== "ready");
  const shown = ready.slice(0, 8);
  const more = ready.length - shown.length;
  const palletLabel = (ev.pallets || []).map(p => p.short_id || `P${p.pallet_num}`).join(", ");
  const geotagged = ready.filter(p => p.gps_lat != null).length;
  return (
    <div className="border rounded p-2 mb-2 bg-light">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <span className={`badge ${ev.kind === "delivery" ? "bg-primary" : ev.kind === "pickup" ? "bg-info text-dark" : "bg-secondary"}`}>{KIND_LABEL[ev.kind] || ev.kind}</span>
        <span className="fw-bold">{ev.occurred_at ? fmtShop(ev.occurred_at, { year: true }) : <span className="text-muted fst-italic">time not set</span>}</span>
        {ev.received_by && <span className="small">received by <b>{ev.received_by}</b></span>}
        <span className="small text-muted">{palletLabel}</span>
        {ev.manifest_display_name
          ? <span className="badge bg-success-subtle text-success border border-success" title="Signed manifest linked"><i className="bi bi-file-earmark-check me-1"></i>manifest linked</span>
          : <span className="badge bg-warning-subtle text-warning-emphasis border border-warning" title="Link the scanned manifest with Edit">no manifest yet</span>}
        {geotagged > 0 && <span className="small text-muted" title="Photos carrying GPS from the phone"><i className="bi bi-geo-alt me-1"></i>{geotagged}/{ready.length} geotagged</span>}
        <div className="ms-auto d-flex flex-wrap gap-1">
          <button className="btn btn-sm btn-outline-primary" disabled={!!busy} onClick={onAddPhotos} title="Add more photos to this record"><i className="bi bi-plus-lg me-1"></i>Photos</button>
          <button className="btn btn-sm btn-outline-secondary" disabled={!!busy} onClick={onEdit} title="Receiver, time, notes, pallets, manifest link"><i className="bi bi-pencil me-1"></i>Edit</button>
          <button className="btn btn-sm btn-light border text-danger" disabled={!!busy} onClick={onPacket} title="Cover, signed manifest, photo sheet with times and GPS, chain of custody">{busy === `packet_${ev.event_id}` ? <><span className="spinner-border spinner-border-sm me-1" />Building…</> : <><i className="bi bi-file-pdf me-1"></i>Packet</>}</button>
          <button className="btn btn-sm btn-outline-secondary" disabled={!!busy} onClick={onSend} title="Email the packet through the SO email log"><i className="bi bi-envelope me-1"></i>Email</button>
          <button className="btn btn-sm btn-outline-danger" disabled={!!busy} onClick={onHide} title="Hide this record with a reason (nothing is deleted)"><i className="bi bi-eye-slash"></i></button>
        </div>
      </div>
      {ev.notes && <div className="small text-muted mb-2"><i className="bi bi-chat-left-text me-1"></i>{ev.notes}</div>}
      <div className="d-flex flex-wrap gap-2 align-items-end">
        {shown.map((p, i) => (
          <button key={p.photo_id} type="button" className="btn p-0 border rounded overflow-hidden bg-white" style={{ width: 112, height: 84 }}
            onClick={() => onOpen(ready.indexOf(p))} title={`${p.original_filename || "photo"}${p.taken_at ? ` · ${fmtShop(p.taken_at)}` : ""}`}>
            {p.thumb_url
              ? <img src={p.thumb_url} alt={p.original_filename || "photo"} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span className="small text-muted">(unavailable)</span>}
          </button>
        ))}
        {more > 0 && (
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => onOpen(shown.length)}>+{more} more</button>
        )}
        {pending.map(p => (
          <span key={p.photo_id} className={`badge ${p.status === "failed" ? "bg-danger" : "bg-secondary"}`} title={p.process_error || "waiting for upload"}>
            {p.status === "failed" ? "failed" : "pending"}: {p.original_filename}
          </span>
        ))}
        {ready.length === 0 && pending.length === 0 && <span className="small text-muted fst-italic">No photos on this record.</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function PodUploadModal({ apiBase, planId, salesOrderId, orderNumber, pallets, fulfillmentType, mode, event, limits, onClose, onDone }) {
  const isNew = mode === "new";
  const isEdit = mode === "edit";
  const [kind, setKind] = useState(event?.kind || defaultKindFor(fulfillmentType));
  const [palletIds, setPalletIds] = useState(() =>
    event ? (event.pallets || []).map(p => p.pallet_id) : pallets.map(p => p.pallet_id));
  const [receivedBy, setReceivedBy] = useState(event?.received_by || "");
  const [occurredAt, setOccurredAt] = useState(() => {
    if (!event?.occurred_at) return "";
    const d = new Date(event.occurred_at);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [notes, setNotes] = useState(event?.notes || "");
  const [docs, setDocs] = useState([]);
  const [manifestDocId, setManifestDocId] = useState(event?.signed_manifest_document_id || "");
  const [files, setFiles] = useState([]);       // {file, kind, status, progress, error, photo_id}
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const maxFiles = limits?.max_photos_per_event || 30;
  const maxBytes = limits?.max_file_bytes || 25 * 1024 * 1024;
  const heicOk = limits?.heic_supported !== false;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/sales_orders/${salesOrderId}/documents`);
        const list = res.ok ? await res.json() : [];
        if (!alive) return;
        setDocs(list);
        if (isNew && !manifestDocId) {
          const m = list.find(isManifestDoc);
          if (m) setManifestDocId(m.document_id);
        }
      } catch (e) { /* the picker just stays empty */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, salesOrderId]);

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    setFiles(prev => {
      const next = [...prev];
      for (const f of incoming) {
        if (next.length >= maxFiles) break;
        const name = (f.name || "").toLowerCase();
        const isPdf = f.type === "application/pdf" || name.endsWith(".pdf");
        const isHeic = name.endsWith(".heic") || name.endsWith(".heif") || f.type === "image/heic" || f.type === "image/heif";
        let err = "";
        if (f.size > maxBytes) err = `${fmtBytes(f.size)} is over the ${fmtBytes(maxBytes)} limit`;
        else if (isHeic && !heicOk) err = "HEIC is not supported on this server yet — export as JPEG";
        next.push({ file: f, kind: isPdf ? "manifest" : "photo", status: err ? "rejected" : "queued", progress: 0, error: err, photo_id: null });
      }
      return next;
    });
  };

  const removeFile = (i) => setFiles(prev => prev.filter((_, j) => j !== i));
  const toggleManifest = (i) => setFiles(prev => prev.map((f, j) => j === i ? { ...f, kind: f.kind === "manifest" ? "photo" : "manifest" } : f));
  const togglePallet = (id) => setPalletIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const submit = async () => {
    setError("");
    const queued = files.map((f, i) => ({ ...f, i })).filter(f => f.status === "queued" || f.status === "failed");
    if (!isEdit && queued.length === 0 && isNew) {
      if (!window.confirm("No photos selected. Create the record without photos?")) return;
    }
    if (isNew && palletIds.length === 0) { setError("Pick at least one pallet."); return; }
    const pin = await pinPrompt(isEdit ? "Enter your PIN to save changes:" : "Enter your PIN to record the delivery:");
    if (!pin) return;
    setSubmitting(true);
    try {
      await saveDeliveryRecord({
        planId, mode, event, pin,
        fields: { kind, palletIds, occurredAt, receivedBy, notes, manifestDocId },
        uploads: queued.map(f => ({ file: f.file, kind: f.kind })),
        onFile: (k, patch) => setFiles(prev => prev.map((x, j) => (j === queued[k].i ? { ...x, ...patch } : x))),
      });
      const stillFailed = files.some(f => f.status === "failed");
      if (!stillFailed) {
        await onDone();
      } else {
        setError("Some photos failed — fix or remove them and submit again; the record itself is saved.");
        setSubmitting(false);
      }
    } catch (e) {
      setError(e.message || String(e));
      setSubmitting(false);
    }
  };

  // After a partial failure the failed rows stay in the list with their error;
  // "Save" runs again for queued/failed rows only.
  const anyBusy = submitting || files.some(f => f.status === "uploading" || f.status === "processing");

  return (
    <Modal show onHide={anyBusy ? undefined : onClose} size="lg" backdrop="static">
      <Modal.Header closeButton={!anyBusy}>
        <Modal.Title className="h6 mb-0">
          {isNew ? "Add delivery photos" : isEdit ? "Edit delivery record" : "Add photos to this record"}
          {orderNumber && <span className="text-muted fw-normal ms-2">{orderNumber}</span>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger py-2 small">{error}</div>}
        {(isNew || isEdit) && (
          <div className="row g-2 mb-2">
            <div className="col-md-4">
              <label className="form-label small mb-0">Kind</label>
              <select className="form-select form-select-sm" value={kind} onChange={e => setKind(e.target.value)} disabled={anyBusy}>
                <option value="delivery">Delivery (we delivered)</option>
                <option value="pickup">Customer pickup</option>
                <option value="carrier_handoff">Carrier handoff</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label small mb-0">Received by</label>
              <input className="form-control form-control-sm" placeholder="name on the manifest" value={receivedBy} onChange={e => setReceivedBy(e.target.value)} disabled={anyBusy} />
            </div>
            <div className="col-md-4">
              <label className="form-label small mb-0">When <span className="text-muted">(blank = from the photos)</span></label>
              <input type="datetime-local" className="form-control form-control-sm" value={occurredAt} onChange={e => setOccurredAt(e.target.value)} disabled={anyBusy} />
            </div>
            <div className="col-md-6">
              <label className="form-label small mb-0">Pallets covered</label>
              <div className="d-flex flex-wrap gap-2">
                {pallets.map(p => (
                  <label key={p.pallet_id} className="form-check form-check-inline small mb-0">
                    <input type="checkbox" className="form-check-input" checked={palletIds.includes(p.pallet_id)} onChange={() => togglePallet(p.pallet_id)} disabled={anyBusy} />
                    <span className="form-check-label">{p.short_id || `P${p.pallet_num}`} <span className="text-muted">({p.status})</span></span>
                  </label>
                ))}
                {pallets.length === 0 && <span className="small text-muted">No shipped or staged pallets.</span>}
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label small mb-0">Signed manifest (document on this SO)</label>
              <select className="form-select form-select-sm" value={manifestDocId || ""} onChange={e => setManifestDocId(e.target.value)} disabled={anyBusy}>
                <option value="">— not on file yet —</option>
                {docs.map(d => <option key={d.document_id} value={d.document_id}>{d.display_name || d.original_filename}{d.doc_type ? ` (${d.doc_type})` : ""}</option>)}
              </select>
            </div>
            <div className="col-12">
              <label className="form-label small mb-0">Notes</label>
              <input className="form-control form-control-sm" placeholder="e.g. left at dock 3, receiver counted 4 pallets" value={notes} onChange={e => setNotes(e.target.value)} disabled={anyBusy} />
            </div>
          </div>
        )}
        {!isEdit && (
          <>
            <div
              className={`border rounded p-3 text-center ${dragOver ? "border-primary bg-primary-subtle" : "border-secondary-subtle bg-light"}`}
              style={{ borderStyle: "dashed", cursor: anyBusy ? "default" : "pointer" }}
              onClick={() => !anyBusy && inputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); if (!anyBusy) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); if (!anyBusy) addFiles(e.dataTransfer.files); }}
            >
              <i className="bi bi-images fs-3 text-primary d-block"></i>
              <div className="small">Drop photos here, or click to pick. JPEG, HEIC, PNG or WebP{heicOk ? "" : " (HEIC not supported on this server)"}; a PDF counts as the signed manifest.</div>
              <div className="small text-muted">Up to {maxFiles} per record, {fmtBytes(maxBytes)} each. The originals are kept exactly as uploaded.</div>
              <input ref={inputRef} type="file" multiple accept="image/*,.heic,.heif,application/pdf" hidden
                onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
            </div>
            {files.length > 0 && (
              <ul className="list-group list-group-flush mt-2 small">
                {files.map((f, i) => (
                  <li key={i} className="list-group-item px-0 py-1 d-flex align-items-center gap-2">
                    <span className={`badge ${f.kind === "manifest" ? "bg-success" : "bg-secondary"}`} style={{ minWidth: 68 }}>{f.kind === "manifest" ? "manifest" : "photo"}</span>
                    <span className="text-truncate" style={{ maxWidth: 260 }} title={f.file.name}>{f.file.name}</span>
                    <span className="text-muted">{fmtBytes(f.file.size)}</span>
                    {f.status === "queued" && (
                      <label className="form-check form-check-inline mb-0 ms-1">
                        <input type="checkbox" className="form-check-input" checked={f.kind === "manifest"} onChange={() => toggleManifest(i)} disabled={anyBusy} />
                        <span className="form-check-label text-muted">this is the signed manifest</span>
                      </label>
                    )}
                    <span className="ms-auto d-flex align-items-center gap-2">
                      {f.status === "uploading" && <div className="progress" style={{ width: 120, height: 8 }}><div className="progress-bar" style={{ width: `${f.progress}%` }} /></div>}
                      {f.status === "processing" && <span className="text-muted"><span className="spinner-border spinner-border-sm me-1"></span>reading photo…</span>}
                      {f.status === "done" && <span className="text-success"><i className="bi bi-check2-circle"></i> saved</span>}
                      {f.status === "failed" && <span className="text-danger" title={f.error}>failed: {f.error}</span>}
                      {f.status === "rejected" && <span className="text-danger">{f.error}</span>}
                      {(f.status === "queued" || f.status === "rejected" || f.status === "failed") && !anyBusy && (
                        <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => removeFile(i)} title="Remove"><i className="bi bi-x-lg"></i></button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <span className="me-auto small text-muted">Uploads go to private storage; only people signed in to JobQ can open them.</span>
        <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={anyBusy}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={anyBusy || (!isEdit && files.every(f => f.status === "rejected") && files.length > 0)}>
          {anyBusy ? <><span className="spinner-border spinner-border-sm me-1"></span>Working…</> : (isEdit ? "Save changes" : isNew ? "Record delivery" : "Upload")}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function PodLightbox({ apiBase, planId, event, index, onClose, onChanged }) {
  const ready = event.photos.filter(p => p.status === "ready");
  const [i, setI] = useState(Math.min(Math.max(index || 0, 0), Math.max(ready.length - 1, 0)));
  const [busy, setBusy] = useState(false);
  const p = ready[i];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") setI(x => Math.min(x + 1, ready.length - 1));
      if (e.key === "ArrowLeft") setI(x => Math.max(x - 1, 0));
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ready.length, onClose]);
  if (!p) return null;

  const hide = async () => {
    const reason = window.prompt("Hide this photo? Nothing is deleted. Reason (required):");
    if (!reason || !reason.trim()) return;
    const pin = await pinPrompt("Enter your PIN to hide this photo:");
    if (!pin) return;
    setBusy(true);
    try {
      await apiJson(`${apiBase}/packaging/plans/${planId}/pod/events/${event.event_id}/photos/${p.photo_id}/hide`, {
        method: "POST", body: JSON.stringify({ pin, reason: reason.trim() }),
      });
      onClose();
      if (onChanged) await onChanged();
    } catch (e) {
      window.alert(e.message || String(e));
    } finally { setBusy(false); }
  };

  const caption = async () => {
    const text = window.prompt("Caption for this photo:", p.caption || "");
    if (text === null) return;
    const pin = await pinPrompt("Enter your PIN:");
    if (!pin) return;
    setBusy(true);
    try {
      await apiJson(`${apiBase}/packaging/plans/${planId}/pod/events/${event.event_id}/photos/${p.photo_id}`, {
        method: "PATCH", body: JSON.stringify({ pin, caption: text }),
      });
      if (onChanged) await onChanged();
      onClose();
    } catch (e) {
      window.alert(e.message || String(e));
    } finally { setBusy(false); }
  };

  return (
    <Modal show onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title className="h6 mb-0">
          {p.original_filename || "photo"} <span className="text-muted fw-normal">· {i + 1} of {ready.length}</span>
          {p.kind === "manifest" && <span className="badge bg-success ms-2">signed manifest</span>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center bg-dark">
        {p.display_url
          ? <img src={p.display_url} alt={p.original_filename || "photo"} style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }} />
          : <div className="text-light p-5">(image unavailable — the link may have expired; close and reopen)</div>}
      </Modal.Body>
      <Modal.Footer className="d-flex flex-wrap align-items-center gap-2">
        <div className="me-auto small">
          <div>
            <b>{p.taken_at ? `Taken ${fmtShop(p.taken_at, { year: true })}` : "No camera timestamp"}</b>
            {p.camera && <span className="text-muted"> · {p.camera}</span>}
            {p.gps_lat != null && (
              <span className="text-muted"> · <a href={p.map_url} target="_blank" rel="noreferrer">{p.gps_lat.toFixed(5)}, {p.gps_lon.toFixed(5)}</a>{p.gps_accuracy_m ? ` ±${Math.round(p.gps_accuracy_m)} m` : ""}</span>
            )}
          </div>
          <div className="text-muted">
            {p.width}×{p.height} · {fmtBytes(p.bytes)} · uploaded {fmtShop(p.uploaded_at)}{p.uploaded_by ? ` by ${p.uploaded_by}` : ""}
            {p.caption && <span> · <i>{p.caption}</i></span>}
          </div>
        </div>
        <button className="btn btn-sm btn-outline-secondary" disabled={i === 0} onClick={() => setI(i - 1)}><i className="bi bi-chevron-left"></i></button>
        <button className="btn btn-sm btn-outline-secondary" disabled={i >= ready.length - 1} onClick={() => setI(i + 1)}><i className="bi bi-chevron-right"></i></button>
        {p.original_url && <a className="btn btn-sm btn-outline-secondary" href={p.original_url} target="_blank" rel="noreferrer" title="The file exactly as uploaded"><i className="bi bi-download me-1"></i>Original</a>}
        <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={caption}><i className="bi bi-chat-left-text me-1"></i>Caption</button>
        <button className="btn btn-sm btn-outline-danger" disabled={busy} onClick={hide} title="Hide with a reason; nothing is deleted"><i className="bi bi-eye-slash me-1"></i>Hide</button>
      </Modal.Footer>
    </Modal>
  );
}
